import { useAppForm } from '@/lib/use-app-form'
import * as v from 'valibot'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery, skipToken } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { SocialButtons } from '../social-buttons'
import { trpc } from '@/main'
import { registerSchema } from 'shared/applicant-form'
import type { LinkErrorCode } from 'shared'
import { AuthCard } from '@/routes/_app/-components/shared/auth-card'
import { Separator } from '@/components/ui/separator'
import { saveCredential } from '@/lib/save-credentials'
import { flowViewTransition } from '@/lib/utils'

export function ApplicantRegisterForm(props: {
  invitationToken?: string
  linkError?: LinkErrorCode
  // The account this browser is signed in to, when the invitation is someone else's: confirming replaces it.
  signedInAs?: string
}) {
  const token = props.invitationToken
  // A `?ref=` token can be a set-password token (employee stub) or a company invitation (candidate
  // invited to take the test). Classify it so we render the right form instead of treating every ref
  // as a password token (which broke the candidate flow — the invitation token isn't a password one).
  // query: direct resolves a registration token held in client state; nothing to prefetch before it exists
  const { data, isLoading, isError } = useQuery(trpc.auth.resolveRegistrationToken.queryOptions(token ?? skipToken))

  if (!token) return <SelfRegisterForm />
  if (isLoading) return null
  // Employee stub confirming their registration: set a password + personal email.
  if (data?.kind === 'password')
    return <EmployeeInvitationConfirm token={token} linkError={props.linkError} signedInAs={props.signedInAs} />
  // Invited candidate: a normal self-registration, prefilled+locked to the invited email so the
  // post-test invitation match (server: processPendingInvitationsByEmail) attaches them to the offer.
  if (data?.kind === 'invitation') return <SelfRegisterForm prefillEmail={data.email} signedInAs={props.signedInAs} />
  if (isError || data?.kind === 'invalid') return <InvalidInvitationNotice />
  return null
}

function SessionSwitchNotice(props: { signedInAs?: string }) {
  const { t } = useLingui()
  if (!props.signedInAs) return null
  const account = props.signedInAs
  return (
    <p role="status" className="bg-muted text-muted-foreground rounded-lg px-3 py-2 text-sm text-pretty">
      {t`You're signed in as ${account}. Continuing will sign you out of that account and into this one.`}
    </p>
  )
}

function InvalidInvitationNotice() {
  const { t } = useLingui()
  return (
    <AuthCard>
      <p className="text-destructive text-sm">{t`This invitation link is invalid or has expired`}</p>
    </AuthCard>
  )
}

/**
 * Employee invited by a company, confirming their registration. The screen resolves the token to the
 * company email and, crucially, the active onboarding mode:
 *  - direct link (default): the company email IS the login — the employee only sets a password;
 *  - legacy consent flow (EMPLOYEE_CONSENT_FLOW): they also pick their own personal email, which
 *    becomes the login so their access survives offboarding.
 * `directLink` comes from the server (checkPasswordToken) so a single env flag drives both ends.
 */
function EmployeeInvitationConfirm(props: { token: string; linkError?: LinkErrorCode; signedInAs?: string }) {
  const [resolved, setResolved] = useState<{ email?: string; directLink: boolean }>()
  const [tokenError, setTokenError] = useState(false)
  const { mutateAsync: checkToken } = useMutation(trpc.auth.checkPasswordToken.mutationOptions())

  useEffect(() => {
    checkToken(props.token)
      .then((res) => setResolved({ email: res.email ?? undefined, directLink: res.directLink }))
      .catch(() => setTokenError(true))
  }, [props.token, checkToken])

  if (tokenError) return <InvalidInvitationNotice />
  // Still resolving the token → render nothing (same as the parent's loading state).
  if (!resolved) return null
  return resolved.directLink ? (
    <DirectLinkEmployeeConfirm token={props.token} email={resolved.email} signedInAs={props.signedInAs} />
  ) : (
    <LegacyEmployeeConfirm
      token={props.token}
      email={resolved.email}
      linkError={props.linkError}
      signedInAs={props.signedInAs}
    />
  )
}

// Direct-link default: the company-assigned email is the login (shown locked); the employee only
// chooses a password. No personal-email field and no social login — the company wants its people
// reachable at the address it provided, not one of their own choosing.
function DirectLinkEmployeeConfirm(props: { token: string; email?: string; signedInAs?: string }) {
  const { t } = useLingui()
  const navigate = useNavigate()
  const { mutateAsync: confirmRegistration } = useMutation(trpc.auth.confirmEmployeeRegistration.mutationOptions())
  const [error, setError] = useState(false)

  const form = useAppForm({
    validators: {
      onChange: v.object({ password: v.pipe(v.string(), v.minLength(3, t`Password must be at least 3 characters`)) }),
    },
    defaultValues: { password: '' },
    onSubmit: async ({ value }) => {
      const email = props.email
      if (!email) return
      setError(false)
      // Confirming SIGNS THEM IN in the same request (see the confirmEmployeeRegistration procedure), so
      // there's no follow-up login to lose a connection in the middle of.
      const result = await confirmRegistration({ token: props.token, password: value.password })
      if (result.status !== 'completed' && result.status !== 'linked') {
        setError(true)
        return
      }
      // The email here is read-only and JS-prefilled (the user only types the password), so Chrome's
      // save-password heuristic doesn't treat it as a user-entered username and never offers to save
      // — unlike the editable-email register forms. Ask for the save explicitly via the credential API.
      await saveCredential(email, value.password)
      // `store()` resolves when the credential is QUEUED, not when the person answers the browser's save
      // bubble, and an SPA history navigation in the same task dismisses it. Yield a frame so the prompt
      // is on screen first.
      await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)))
      navigate({ to: '/test' })
    },
  })

  return (
    <AuthCard
      title={t`Confirm your registration`}
      subtitle={t`Set a password to complete your D'Anchiano account`}
      contentClassName="flex flex-col gap-5"
    >
      <SessionSwitchNotice signedInAs={props.signedInAs} />
      <form
        onSubmit={(event) => {
          event.preventDefault()
          form.handleSubmit()
        }}
        className="flex flex-col gap-3"
      >
        {/* Company email shown locked — it's the login, the person can't change it. */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t`Your company email`}</span>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            value={props.email ?? ''}
            readOnly
            className="border-input bg-muted/50 text-muted-foreground h-8 cursor-not-allowed rounded-lg border px-2.5 py-1 text-sm"
          />
        </div>

        <form.AppField
          name="password"
          children={(field) => <field.PasswordField label={t`Password`} autoComplete="new-password" />}
        />

        {error && <p className="text-destructive text-sm">{t`Something went wrong. Please try again.`}</p>}

        <form.AppForm>
          <form.SubmitButton label={t`Confirm`} />
        </form.AppForm>
      </form>
    </AuthCard>
  )
}

// Legacy consent flow (EMPLOYEE_CONSENT_FLOW): confirm by setting a password and their own personal
// email (the company email comes locked from the token). If that personal email or social account
// already belongs to an account they own, the company is linked into it and its test recovered
// instead of creating a duplicate (server validates ownership inline; see confirmEmployeeRegistration).
function LegacyEmployeeConfirm(props: {
  token: string
  email?: string
  linkError?: LinkErrorCode
  signedInAs?: string
}) {
  const { t } = useLingui()
  const navigate = useNavigate()
  const email = props.email
  // A link guard message — from a failed submit, or from the social-login callback redirect.
  const [linkError, setLinkError] = useState<LinkErrorCode | undefined>(props.linkError)

  const { mutateAsync: confirmRegistration } = useMutation(trpc.auth.confirmEmployeeRegistration.mutationOptions())

  const linkErrorMessage = (code: LinkErrorCode) => {
    switch (code) {
      case 'use-personal-email':
        return t`That's the company email. Enter your own personal email so your account stays yours.`
      case 'use-personal-account':
        return t`Please sign in with your personal account, not your company's, so you don't lose your data.`
      case 'password-required':
        return t`An account already exists with this email. Enter its password to link it and keep your test results, or reset your password.`
      case 'link-personal-account':
        return t`We've recognized your company account. Now sign in with your personal account — by email and password above, or with a personal social login — to link it and keep your data.`
      case 'too-many-attempts':
        return t`Too many incorrect password attempts. Please wait a few minutes before trying again, or reset your password.`
      case 'link-failed':
        return t`We couldn't link your account. Please try again.`
      default:
        return t`Something went wrong. Please try again.`
    }
  }

  const form = useAppForm({
    validators: {
      onChange: v.object({
        password: v.pipe(v.string(), v.minLength(3, t`Password must be at least 3 characters`)),
        personalEmail: v.pipe(v.string(), v.email()),
      }),
    },
    defaultValues: { password: '', personalEmail: '' },
    onSubmit: async ({ value }) => {
      if (!email) return
      setLinkError(undefined)
      // Mirror the server guard for instant feedback: the personal email can't be the company email.
      if (value.personalEmail.toLowerCase() === email.toLowerCase()) {
        setLinkError('use-personal-email')
        return
      }
      // The server completes a new account, links into an existing one (rescuing its test), or returns
      // a guard code we surface inline — and on success it SIGNS THEM IN in the same request, so there
      // is no follow-up login to lose a connection in the middle of.
      const result = await confirmRegistration({
        token: props.token,
        password: value.password,
        personalEmail: value.personalEmail,
      })
      if (result.status !== 'completed' && result.status !== 'linked') {
        setLinkError(result.status)
        return
      }
      // Store the PERSONAL email: that's the login they keep after leaving the company, even though the
      // session was established through the company alias (it carries the employer context).
      await saveCredential(value.personalEmail, value.password)
      // A linked account already has its test → go to the report; a brand-new account takes the test.
      navigate({ to: result.status === 'linked' ? '/report' : '/test' })
    },
  })

  return (
    <AuthCard
      title={t`Confirm your registration`}
      subtitle={t`Use your personal account, not your company's — this keeps your profile and test results yours even if you leave the company.`}
      contentClassName="flex flex-col gap-5"
    >
      <SessionSwitchNotice signedInAs={props.signedInAs} />
      {/* Carries the invite token so the callback links this company into the resolved account. */}
      <SocialButtons linkToken={props.token} />

      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-muted-foreground text-xs">{t`or`}</span>
        <Separator className="flex-1" />
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          form.handleSubmit()
        }}
        className="flex flex-col gap-3"
      >
        {/* Company email shown locked. autoComplete off so it doesn't compete with the
                personal-email field for autofill. */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t`Your company email`}</span>
          <input
            type="email"
            autoComplete="off"
            value={email ?? ''}
            readOnly
            className="border-input bg-muted/50 text-muted-foreground h-8 cursor-not-allowed rounded-lg border px-2.5 py-1 text-sm"
          />
        </div>

        {/* Personal email becomes the primary login, so the person keeps access even if the
                company removes them. autoComplete="username" lets the browser offer a saved login. */}
        <form.AppField
          name="personalEmail"
          children={(field) => (
            <field.TextField
              label={t`Your personal email`}
              type="email"
              autoComplete="username"
              description={t`You'll use this to sign in and keep access to your results even if you leave the company`}
            />
          )}
        />

        <form.AppField
          name="password"
          children={(field) => <field.PasswordField label={t`Password`} autoComplete="current-password" />}
        />

        {linkError && (
          <p
            className={`text-sm ${linkError === 'link-personal-account' ? 'text-muted-foreground' : 'text-destructive'}`}
          >
            {linkErrorMessage(linkError)}{' '}
            {(linkError === 'password-required' || linkError === 'too-many-attempts') && (
              <Link to="/begin-password-reset" className="text-primary font-medium hover:underline">
                {t`Reset password`}
              </Link>
            )}
          </p>
        )}

        <form.AppForm>
          <form.SubmitButton label={t`Confirm`} />
        </form.AppForm>
      </form>
    </AuthCard>
  )
}

function SelfRegisterForm(props: { prefillEmail?: string; signedInAs?: string } = {}) {
  const { t } = useLingui()
  const navigate = useNavigate()
  // Post-auth destination carried in from the compare CTA; forwarded into the test flow so it survives
  // the whole onboarding and lands the new professional back on the profile they wanted to compare.
  const { redirect } = useSearch({ strict: false })
  const { mutateAsync: createUser } = useMutation(trpc.auth.createUser.mutationOptions())

  // When the registration came from a company invitation, the email is fixed to the invited address
  // (the company's traceability) and read-only — that's the address the post-test match keys on.
  const lockEmail = !!props.prefillEmail

  const form = useAppForm({
    validators: { onChange: registerSchema },
    defaultValues: {
      email: props.prefillEmail ?? '',
      password: '',
      termsOfUse: false,
    },
    onSubmit: async ({ value }) => {
      await createUser(value)
      await saveCredential(value.email, value.password)
      // viewTransition: the register form and the /test step share the `flow-header` / `flow-frame`
      // identities, so the box + header morph into the first test step instead of hard-cutting.
      navigate({ to: '/test', search: { redirect }, viewTransition: flowViewTransition() })
    },
  })

  return (
    <AuthCard
      home
      title={t`Professional registration`}
      subtitle={t`Create your D'Anchiano account`}
      contentClassName="flex flex-col gap-5"
      footer={
        <>
          {t`Already have an account?`}{' '}
          <Link to="/login" search={{ redirect }} className="text-primary font-medium hover:underline">
            {t`Login`}
          </Link>
        </>
      }
    >
      <SessionSwitchNotice signedInAs={props.signedInAs} />
      {/* When invited by a company the email is fixed to the invited address, so social sign-in
              (which would authenticate a self-chosen Google account) is hidden — it would register a
              different email and break the invitation/match. Self-registration keeps the Google option. */}
      {!lockEmail && (
        <>
          <SocialButtons register />

          <div className="flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-muted-foreground text-xs">{t`or`}</span>
            <Separator className="flex-1" />
          </div>
        </>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault()
          form.handleSubmit()
        }}
        className="flex flex-col gap-3"
      >
        {/* type="email" + autoComplete="username" pairs with the new-password field below so
                Chrome recognizes this as a sign-up form and offers to save the credentials. */}
        <form.AppField
          name="email"
          children={(field) => (
            <field.TextField label={t`Email`} type="email" autoComplete="username" readOnly={lockEmail} />
          )}
        />
        <form.AppField
          name="password"
          children={(field) => <field.PasswordField label={t`Password`} autoComplete="new-password" />}
        />
        <form.AppField
          name="termsOfUse"
          children={(field) => (
            <field.CheckboxField
              label={
                <div className="text-xs">
                  <Trans>
                    I accept the{' '}
                    <Link to="/terms-of-use" className="underline" target="_blank" rel="noopener noreferrer">
                      Terms of use
                    </Link>{' '}
                    and the{' '}
                    <Link to="/privacy-policy" className="underline" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </Link>
                  </Trans>
                </div>
              }
            />
          )}
        />

        <form.AppForm>
          <form.SubmitButton label={t`Create account`} />
        </form.AppForm>
      </form>
    </AuthCard>
  )
}
